import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, analytics } from "@/lib/utils";
import { getLocationDisplayName, LOCATION_FORM_ORDER } from "@/lib/locationUrls";
import { SEO } from "@/components/SEO";
import { TurnstileWidget } from "@/components/TurnstileWidget";

const CONTACT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Jersey Girls Fitness",
  "url": "https://jerseygirlsclubs.com",
  "telephone": "(856) 382-0555",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "(856) 382-0555",
    "contactType": "customer service",
    "areaServed": "US",
    "availableLanguage": "English"
  }
};

export default function ContactPage() {
  const [locations, setLocations] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const pageLoadTimeRef = useRef(Date.now());
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    location_id: "",
    message: "",
    website: "" // honeypot – leave empty
  });

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error("Failed to fetch locations:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) return; // honeypot – bot trap
    if (!turnstileToken) {
      toast.error("Please complete the verification");
      return;
    }

    if (!formData.name || !formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API_URL}/contact`, { ...formData, time_on_page: Date.now() - pageLoadTimeRef.current, "cf-turnstile-response": turnstileToken });
      analytics.trackContactSubmit();
      setIsSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      console.error("Failed to submit contact form:", error);
      const data = error.response?.data;
      const detail = data?.detail;
      const serverError = data?.error;
      let msg = typeof detail === "string" ? detail : "Failed to send message. Please try again.";
      if (typeof serverError === "string" && serverError && serverError !== msg) {
        msg += ` (${serverError})`;
      }
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center px-4" data-testid="contact-success">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-md"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h2 className="text-3xl font-heading font-bold text-white mb-4">MESSAGE SENT!</h2>
          <p className="text-gray-600 mb-8">
            Thank you for reaching out. Our team will get back to you as soon as possible.
          </p>
          <Button 
            onClick={() => window.location.href = "/"}
            className="bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider"
          >
            Back to Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      <SEO
        title="Contact Us"
        description="Have a question or comment? Contact Jersey Girls. Call (856) 382-0555 or fill out our contact form. We're here to help you achieve your fitness goals."
        url="/contact-us"
        structuredData={CONTACT_SCHEMA}
      />
      {/* Header */}
      <section className="py-12 md:py-20 px-4 md:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-heading font-black text-gray-900 mb-4" data-testid="contact-heading">
              CONTACT US
            </h1>
            <p className="text-gray-700 text-lg">
              Have a question or comment? We'd love to hear from you. Fill out the form below and our team will get back to you shortly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4 md:px-8">
        <div className="max-w-xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div className="form-group absolute -left-[9999px] w-1 h-1 overflow-hidden" aria-hidden="true">
                <Label htmlFor="contact-website">Website</Label>
                <Input id="contact-website" name="website" type="text" value={formData.website} onChange={handleChange} tabIndex={-1} autoComplete="off" />
              </div>
              <div className="form-group">
                <Label htmlFor="name" className="form-label">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-gray-300 focus:border-brand-pink text-gray-900"
                  placeholder="Your full name"
                  data-testid="contact-name-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="email" className="form-label">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-gray-300 focus:border-brand-pink text-gray-900"
                  placeholder="your@email.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="phone" className="form-label">Phone (Optional)</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="h-12 bg-white border-gray-300 focus:border-brand-pink text-gray-900"
                  placeholder="(123) 456-7890"
                  data-testid="contact-phone-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="location" className="form-label">Location (Optional)</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, location_id: value })}>
                  <SelectTrigger className="h-12 bg-white border-gray-300 focus:border-brand-pink text-gray-900" data-testid="contact-location-select">
                    <SelectValue placeholder="Select a location (optional)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {[...locations]
                      .sort((a, b) => LOCATION_FORM_ORDER.indexOf(a.id) - LOCATION_FORM_ORDER.indexOf(b.id))
                      .map((loc) => (
                        <SelectItem key={loc.id} value={loc.id} className="text-gray-900 hover:bg-gray-100">
                          {getLocationDisplayName(loc.id) ?? loc.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="form-group">
                <Label htmlFor="message" className="form-label">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="bg-white border-gray-300 focus:border-brand-pink text-gray-900 resize-none"
                  placeholder="How can we help you?"
                  data-testid="contact-message-input"
                />
              </div>

              <div className="form-group">
                <TurnstileWidget onVerify={setTurnstileToken} onExpire={() => setTurnstileToken("")} theme="light" />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !turnstileToken}
                className="w-full h-12 bg-brand-pink hover:bg-brand-pink-hover text-white font-bold uppercase tracking-wider shadow-neon-pink"
                data-testid="contact-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
